import {
  BulkDeleteButton,
  Create,
  Datagrid,
  Edit,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
} from "react-admin";
import { CrudPermissions } from "../auth/crud-permissions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { AdSources } from "@arrowsup/platform-dtos";
import { TopHelpInfoText } from "../help/top-help-info-text";

export const RetainerResource = (perms: CrudPermissions) => {
  return (
    <Resource
      name="retainer"
      key="retainer"
      options={{
        label: "Retainers",
      }}
      list={RetainerList(perms)}
      edit={perms.update ? RetainerEdit : undefined}
      create={perms.create ? RetainerCreate : undefined}
      icon={AccountBalanceIcon}
    />
  );
};

export const RetainerList = (perms: CrudPermissions) => {
  return (
    <>
      <TopHelpInfoText text="Track monthly retainer fees by advertising platform for ROI calculations." />
      <List>
        <Datagrid
          rowClick={perms.update ? "show" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="ID" />
          <NumberField
            source="year"
            label="Year"
            options={{ useGrouping: false }}
          />
          <FunctionField
            label="Month"
            render={(record: { month?: number }) => {
              const monthNames = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ];

              const month = record.month;

              if (month && month >= 1 && month <= 12) {
                return monthNames[month - 1];
              }

              return month ?? "";
            }}
          />
          <TextField source="adSource" label="Channel" />
          <NumberField
            source="fee"
            label="Fee"
            options={{ style: "currency", currency: "USD" }}
          />
        </Datagrid>
      </List>
    </>
  );
};

const AdSourceInput: React.FC = () => {
  const adSourceChoices = [
    ...AdSources.map((v) => ({ id: v, name: v })),
    { id: "other", name: "other" },
  ];

  return (
    <SelectInput
      source="adSource"
      label="Channel"
      choices={adSourceChoices}
      required
      fullWidth
      helperText="Which channel is this retainer for?"
      sx={{ pb: 2 }}
    />
  );
};

const MonthInput: React.FC = () => {
  const monthChoices = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  return (
    <SelectInput
      source="month"
      label="Month"
      choices={monthChoices}
      required
      fullWidth
      helperText="Select the month for this retainer"
      sx={{ pb: 2 }}
    />
  );
};

export const RetainerEdit: React.FC = () => {
  return (
    <Edit redirect="list">
      <SimpleForm>
        <NumberInput
          source="year"
          label="Year"
          min={2020}
          max={2100}
          step={1}
          required
          fullWidth
          sx={{ pb: 2 }}
        />
        <MonthInput />
        <AdSourceInput />
        <NumberInput
          source="fee"
          label="Fee (USD)"
          min={0}
          step={0.01}
          required
          fullWidth
          helperText="Monthly retainer amount in USD"
          sx={{ pb: 2 }}
        />
      </SimpleForm>
    </Edit>
  );
};

export const RetainerCreate: React.FC = () => {
  return (
    <Create redirect="list">
      <SimpleForm>
        <NumberInput
          source="year"
          label="Year"
          min={2020}
          max={2100}
          step={1}
          required
          fullWidth
          sx={{ pb: 2 }}
        />
        <MonthInput />
        <AdSourceInput />
        <NumberInput
          source="fee"
          label="Fee (USD)"
          min={0}
          step={0.01}
          required
          fullWidth
          helperText="Monthly retainer amount in USD"
          sx={{ pb: 2 }}
        />
      </SimpleForm>
    </Create>
  );
};
